import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { formatDateInBusinessTZ, formatTimeInBusinessTZ } from '../../utils/date';
import { senderDisplayName } from '../utils/emailBrand';

interface EmailMetadata {
  template_key?: string;
  template_data?: Record<string, unknown>;
  recipients?: string[];
  from?: string;
  [key: string]: unknown;
}

interface EmailNotification {
  id: string;
  recipient_email: string;
  recipient_name?: string;
  subject: string;
  email_type: string;
  status: string;
  entity_type?: string;
  entity_id?: string;
  sent_at: string;
  created_at: string;
  template_version?: number;
  metadata?: EmailMetadata;
}

type TimeFilter = '24h' | 'week' | 'month';

function getStatusColor(status: string) {
  switch (status) {
    case 'sent':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800';
    case 'recovered':
      return 'bg-teal-100 text-teal-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

const EMAIL_TYPE_LABELS: Record<string, string> = {
  'invoice-created': 'Invoice Created',
  'payment-confirmation': 'Payment Confirmation',
  'payment-failed': 'Payment Failed',
  'enrollment-confirmation': 'Enrollment Confirmation',
  'enrollment-reminder': 'Enrollment Reminder',
  'payment-reminder': 'Payment Reminder',
  'late-fee-notice': 'Late Fee Notice',
  'refund-processed': 'Refund Processed',
  'coupon-applied': 'Coupon Applied',
  'welcome-email': 'Welcome Email',
  'password-reset': 'Password Reset',
  'account-verification': 'Account Verification',
  'general': 'General',
};

function getEmailTypeLabel(type: string) {
  return EMAIL_TYPE_LABELS[type] ?? type;
}

// entity_type values that can be re-triggered via db-webhook INSERT
const RESENDABLE_TABLE: Record<string, string> = {
  invoice: 'invoices',
  student: 'students',
};

const AdminEmailHistory: React.FC = () => {
  const [notifications, setNotifications] = useState<EmailNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('24h');
  const [recipientFilter, setRecipientFilter] = useState('');
  const [emailTypeFilter, setEmailTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [versionFilter, setVersionFilter] = useState<string>('');
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [resendToast, setResendToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);


  useEffect(() => {
    fetchNotifications();
  }, [timeFilter, recipientFilter, emailTypeFilter, statusFilter, versionFilter]);

  const getTimeRange = (filter: TimeFilter) => {
    const now = new Date();
    const startDate = new Date();

    switch (filter) {
      case '24h':
        startDate.setHours(now.getHours() - 24);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
    }

    return { startDate, endDate: now };
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { startDate, endDate } = getTimeRange(timeFilter);

      let query = supabase
        .from('email_notifications')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (recipientFilter) {
        query = query.ilike('recipient_email', `%${recipientFilter}%`);
      }

      if (emailTypeFilter) {
        query = query.eq('email_type', emailTypeFilter);
      }

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      if (versionFilter) {
        query = query.eq('template_version', parseInt(versionFilter));
      }

      const { data, error } = await query;

      if (error) throw error;
      setNotifications(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };




  function canResend(n: EmailNotification): boolean {
    return (n.status === 'failed' || n.status === 'pending')
      && Boolean(n.entity_id)
      && Boolean(RESENDABLE_TABLE[n.entity_type ?? '']);
  }

  async function handleResend(notification: EmailNotification) {
    if (!notification.entity_id || !notification.entity_type) return;
    const table = RESENDABLE_TABLE[notification.entity_type];
    if (!table) return;

    setResendingEmail(notification.id);
    try {
      const { error: invokeError } = await supabase.functions.invoke('db-webhook', {
        body: {
          type: 'INSERT',
          table,
          schema: 'public',
          record: { id: notification.entity_id },
          old_record: null,
        },
      });
      if (invokeError) throw invokeError;

      // Mark the original failed/pending record as recovered so it's clear this
      // was addressed and prevent accidental duplicate resends
      await supabase
        .from('email_notifications')
        .update({ status: 'recovered' })
        .eq('id', notification.id);

      setResendToast({ message: 'Resend triggered — original record marked as recovered', type: 'success' });
      fetchNotifications();
    } catch (err) {
      setResendToast({ message: err instanceof Error ? err.message : 'Failed to trigger resend', type: 'error' });
    } finally {
      setResendingEmail(null);
      setTimeout(() => setResendToast(null), 6000);
    }
  }

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  function renderValue(v: unknown): React.ReactNode {
    if (v === null || v === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }
    if (Array.isArray(v)) {
      return (
        <ul className="space-y-0.5">
          {v.map((item, i) => {
            if (item && typeof item === 'object') {
              const obj = item as Record<string, unknown>;
              if ('description' in obj) {
                const desc = String(obj.description);
                const amt = 'amount' in obj ? ` — $${obj.amount}` : '';
                return <li key={i} className="text-gray-700">{desc}<span className="text-gray-500">{amt}</span></li>;
              }
              return <li key={i} className="font-mono text-gray-600">{JSON.stringify(obj)}</li>;
            }
            return <li key={i}>{String(item)}</li>;
          })}
        </ul>
      );
    }
    if (typeof v === 'string' && UUID_RE.test(v)) {
      return <span title={v} className="font-mono text-gray-500 cursor-help">{v.slice(0, 8)}…</span>;
    }
    if (typeof v === 'object') {
      return <span className="font-mono text-gray-600">{JSON.stringify(v)}</span>;
    }
    return <span>{String(v)}</span>;
  }

  function MetadataTable({ metadata }: { metadata?: EmailMetadata }) {
    const data: Record<string, unknown> = (metadata && (metadata.template_data ?? metadata)) ?? {};
    const entries = Object.entries(data);
    if (entries.length === 0) return <span className="text-gray-400 text-xs">—</span>;
    return (
      <table className="text-[11px] leading-4 border-collapse w-full">
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="align-top border-t border-gray-100 first:border-t-0">
              <td className="pr-3 py-1 text-gray-400 font-medium whitespace-nowrap w-[30%]">{k}</td>
              <td className="py-1 text-gray-800">{renderValue(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">

      {/* Filters - Desktop */}
      <div className="hidden md:flex gap-4 mb-6 p-4 bg-white rounded-lg shadow flex-shrink-0">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time Range
          </label>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipient Email
          </label>
          <input
            type="text"
            value={recipientFilter}
            onChange={(e) => setRecipientFilter(e.target.value)}
            placeholder="Filter by email..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Type
          </label>
          <select
            value={emailTypeFilter}
            onChange={(e) => setEmailTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="invoice-created">Invoice Created</option>
            <option value="payment-confirmation">Payment Confirmation</option>
            <option value="payment-failed">Payment Failed</option>
            <option value="enrollment-confirmation">Enrollment Confirmation</option>
            <option value="enrollment-reminder">Enrollment Reminder</option>
            <option value="payment-reminder">Payment Reminder</option>
            <option value="late-fee-notice">Late Fee Notice</option>
            <option value="refund-processed">Refund Processed</option>
            <option value="coupon-applied">Coupon Applied</option>
            <option value="welcome-email">Welcome Email</option>
            <option value="password-reset">Password Reset</option>
            <option value="account-verification">Account Verification</option>
            <option value="general">General</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="recovered">Recovered</option>
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Template Version
          </label>
          <input
            type="number"
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            placeholder="Filter by version..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Filters - Mobile */}
      <div className="md:hidden mb-6 space-y-3 flex-shrink-0">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Time Range
          </label>
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recipient Email
          </label>
          <input
            type="text"
            value={recipientFilter}
            onChange={(e) => setRecipientFilter(e.target.value)}
            placeholder="Filter by email..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Type
          </label>
          <select
            value={emailTypeFilter}
            onChange={(e) => setEmailTypeFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="invoice-created">Invoice Created</option>
            <option value="payment-confirmation">Payment Confirmation</option>
            <option value="payment-failed">Payment Failed</option>
            <option value="enrollment-confirmation">Enrollment Confirmation</option>
            <option value="enrollment-reminder">Enrollment Reminder</option>
            <option value="payment-reminder">Payment Reminder</option>
            <option value="late-fee-notice">Late Fee Notice</option>
            <option value="refund-processed">Refund Processed</option>
            <option value="coupon-applied">Coupon Applied</option>
            <option value="welcome-email">Welcome Email</option>
            <option value="password-reset">Password Reset</option>
            <option value="account-verification">Account Verification</option>
            <option value="general">General</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
            <option value="recovered">Recovered</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Template Version
          </label>
          <input
            type="number"
            value={versionFilter}
            onChange={(e) => setVersionFilter(e.target.value)}
            placeholder="Filter by version..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Resend toast */}
      {resendToast && (
        <div className={`mb-4 px-4 py-3 rounded text-sm flex-shrink-0 ${resendToast.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-700 border border-red-300'}`}>
          {resendToast.message}
        </div>
      )}

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600 flex-shrink-0">
        Showing {notifications.length} notifications
        {recipientFilter && ` for "${recipientFilter}"`}
        {emailTypeFilter && ` of type "${getEmailTypeLabel(emailTypeFilter)}"`}
        {statusFilter && ` with status "${statusFilter}"`}
        {versionFilter && ` with template version ${versionFilter}`}
        {` in the last ${timeFilter === '24h' ? '24 hours' : timeFilter === 'week' ? 'week' : 'month'}`}
      </div>



      {/* Notifications Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No email notifications found for the selected criteria.
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
            {/* Desktop Table */}
            <table className="hidden md:table min-w-full divide-y divide-gray-200 table-fixed">
              <colgroup>
                <col className="w-[16%]" />
                <col className="w-[17%]" />
                <col className="w-[11%]" />
                <col className="w-[6%]" />
                <col className="w-[29%]" />
                <col className="w-[7%]" />
                <col className="w-[5%]" />
                <col className="w-[9%]" />
              </colgroup>
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variables
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 break-all">
                          {notification.recipient_email}
                        </div>
                        {notification.recipient_name && (
                          <div className="text-sm text-gray-500">
                            {notification.recipient_name}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 break-words" title={notification.subject}>
                        {notification.subject}
                      </div>
                      {senderDisplayName(notification.metadata?.from) && (
                        <div className="text-xs text-gray-500">From {senderDisplayName(notification.metadata?.from)}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {getEmailTypeLabel(notification.email_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {notification.template_version ? (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            v{notification.template_version}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <MetadataTable metadata={notification.metadata} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                        {notification.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{formatDateInBusinessTZ(notification.sent_at)}</div>
                      <div>{formatTimeInBusinessTZ(notification.sent_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {canResend(notification) && (
                        <button
                          onClick={() => handleResend(notification)}
                          disabled={resendingEmail === notification.id}
                          className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {resendingEmail === notification.id ? 'Sending…' : 'Resend'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {notifications.map((notification) => (
                <div key={notification.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">
                        {notification.recipient_email}
                      </div>
                      {notification.recipient_name && (
                        <div className="text-gray-500 text-sm">
                          {notification.recipient_name}
                        </div>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(notification.status)}`}>
                      {notification.status}
                    </span>
                  </div>
                  
                  <div className="mb-2">
                    <div className="text-sm text-gray-900 font-medium">
                      {notification.subject}
                    </div>
                    {senderDisplayName(notification.metadata?.from) && (
                      <div className="text-xs text-gray-500">From {senderDisplayName(notification.metadata?.from)}</div>
                    )}
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <span>{getEmailTypeLabel(notification.email_type)}</span>
                      {notification.template_version && (
                        <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          v{notification.template_version}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      {formatDateInBusinessTZ(notification.sent_at)} at {formatTimeInBusinessTZ(notification.sent_at)}
                    </div>
                    {canResend(notification) && (
                      <button
                        onClick={() => handleResend(notification)}
                        disabled={resendingEmail === notification.id}
                        className="px-2 py-1 text-xs font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resendingEmail === notification.id ? 'Sending…' : 'Resend'}
                      </button>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-gray-700">
                    <div className="font-medium mb-1">Variables</div>
                    <MetadataTable metadata={notification.metadata} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailHistory;
